import { apiKeyManager } from './apiKeyManager'

// Enhanced interfaces for comprehensive mailbox data
export interface SmartleadMailboxDetails {
  id: number
  email: string
  from_name: string
  daily_limit: number
  current_daily_sent: number
  is_smtp_success: boolean
  is_imap_success: boolean
  status: string
  warmup_enabled: boolean
  signature?: string
  avatar_url?: string
  custom_tracking_domain?: string
  reply_to_email?: string
  bcc_email?: string
  timezone?: string
  max_email_per_day?: number
  min_time_btwn_emails?: number
  email_provider?: string
  smtp_host?: string
  smtp_port?: number
  smtp_username?: string
  imap_host?: string
  imap_port?: number
  imap_username?: string
  created_at?: string
  updated_at?: string
  total_sent?: number
  total_replied?: number
  total_bounced?: number
  total_opened?: number
  total_clicked?: number
  reputation_score?: number
  
  // Additional fields from actual Smartlead API response
  type?: string
  user_id?: number
  password?: string
  username?: string
  client_id?: number | null
  imap_password?: string
  campaign_count?: number
  imap_port_type?: string
  smtp_port_type?: string
  warmup_details?: any
  message_per_day?: number
  daily_sent_count?: number
  imap_failure_error?: string | null
  smtp_failure_error?: string | null
  is_different_imap_account?: boolean
  different_reply_to_address?: string | null
  from_email?: string
}

export interface SmartleadWarmupStats {
  email: string
  warmup_reputation: number
  warmup_volume: number
  warmup_enabled: boolean
  daily_warmup_limit: number
  warmup_reply_rate: number
  warmup_folder_percentage: {
    inbox: number
    spam: number
    promotion: number
    social: number
  }
  warmup_age_days?: number
  warmup_sending_volume?: number
  warmup_receiving_volume?: number
}

export interface SmartleadMailboxOverallStats {
  email: string
  total_sent: number
  total_replies: number
  total_bounces: number
  reply_rate: number
  bounce_rate: number
  open_rate: number
  click_rate: number
  unsubscribe_rate?: number
  spam_rate?: number
  positive_replies?: number
  negative_replies?: number
  neutral_replies?: number
  meetings_booked?: number
  interested_replies?: number
}

export interface SmartleadHealthMetrics {
  email: string
  health_score: number
  reputation_score: number
  deliverability_rate: number
  spam_rate: number
  bounce_rate: number
  reply_rate: number
  open_rate: number
  sender_score?: number
  domain_reputation?: number
  ip_reputation?: number
  engagement_score?: number
}

export interface SmartleadCampaignAssociation {
  campaign_id: string
  campaign_name: string
  status: string
  assigned_at: string
  daily_limit?: number
  sequence_position?: number
  last_activity?: string
}

export interface SmartleadProviderPerformance {
  provider: string
  total_sent: number
  total_replies: number
  reply_rate: number
  bounce_rate: number
  health_score: number
  deliverability_rate?: number
  spam_complaints?: number
  unsubscribes?: number
}

export interface EnhancedMailboxData {
  basic_details: SmartleadMailboxDetails
  warmup_stats: SmartleadWarmupStats
  overall_stats: SmartleadMailboxOverallStats
  health_metrics: SmartleadHealthMetrics
  campaign_associations: SmartleadCampaignAssociation[]
  provider_performance: SmartleadProviderPerformance
  in_smartlead: boolean
  last_synced: string
}

export interface MailboxUpdatePayload {
  from_name?: string
  daily_limit?: number
  warmup_enabled?: boolean
  signature?: string
  custom_tracking_domain?: string
  reply_to_email?: string
  bcc_email?: string
  timezone?: string
  max_email_per_day?: number
  min_time_btwn_emails?: number
}

// Progress callback interface for fetching updates
export interface FetchingProgress {
  phase: 'fetching'
  currentBatch: number
  totalFetched: number
  batchSize: number
}

export type FetchingProgressCallback = (progress: FetchingProgress) => void

export class EnhancedSmartleadApi {
  private baseUrl = 'https://server.smartlead.ai/api/v1'
  private apiKey: string | null = null

  constructor() {
    this.apiKey = apiKeyManager.getApiKey()
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    if (!this.apiKey) {
      throw new Error('Smartlead API key is not configured')
    }

    const url = `${this.baseUrl}${endpoint}${endpoint.includes('?') ? '&' : '?'}api_key=${this.apiKey}`
    console.log(`🔗 Smartlead API Request: ${endpoint}`)

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error(`❌ Smartlead API Error (${response.status}):`, errorText)
      throw new Error(`Smartlead API error: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log(`✅ Smartlead API Response received for ${endpoint}`)
    return data
  }

  async findSingleMailbox(targetEmail: string): Promise<{ 
    found: boolean, 
    mailbox?: SmartleadMailboxDetails, 
    searchStats: { totalChecked: number, batchesChecked: number, timeElapsed: number } 
  }> {
    console.log(`🔍 Starting efficient search for email: ${targetEmail}`)
    const startTime = Date.now()
    let totalChecked = 0
    let batchesChecked = 0
    let offset = 0
    const limit = 50 // Smaller batches for faster individual lookups

    try {
      while (true) {
        batchesChecked++
        console.log(`📦 Searching batch ${batchesChecked} (offset: ${offset}, limit: ${limit})...`)

        const data = await this.makeRequest(`/email-accounts?offset=${offset}&limit=${limit}`)
        
        if (!Array.isArray(data) || data.length === 0) {
          console.log(`🏁 No more mailboxes to search. Email ${targetEmail} not found.`)
          break
        }

        totalChecked += data.length
        console.log(`🔍 Checking ${data.length} mailboxes in batch ${batchesChecked}...`)

        // Search for the target email in this batch
        for (const item of data) {
          let emailValue = item.from_email || item.username || item.email || item.email_address || item.mail || item.user_email || item.account_email

          if (emailValue && emailValue.toLowerCase() === targetEmail.toLowerCase()) {
            const timeElapsed = Date.now() - startTime
            console.log(`✅ Found target email ${targetEmail} after checking ${totalChecked} mailboxes in ${timeElapsed}ms`)
            
            return {
              found: true,
              mailbox: item as SmartleadMailboxDetails,
              searchStats: { totalChecked, batchesChecked, timeElapsed }
            }
          }
        }

        // If we got fewer results than requested, we've reached the end
        if (data.length < limit) {
          console.log(`🏁 Reached end of mailboxes. Email ${targetEmail} not found.`)
          break
        }

        offset += limit
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200))
      }

      const timeElapsed = Date.now() - startTime
      console.log(`❌ Email ${targetEmail} not found after checking ${totalChecked} mailboxes in ${timeElapsed}ms`)
      
      return {
        found: false,
        searchStats: { totalChecked, batchesChecked, timeElapsed }
      }

    } catch (error) {
      const timeElapsed = Date.now() - startTime
      console.error(`❌ Error during single email lookup:`, error)
      throw new Error(`Search failed after checking ${totalChecked} mailboxes: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async fetchMailboxDetails(emailAccountId: number): Promise<SmartleadMailboxDetails> {
    try {
      const data = await this.makeRequest(`/email-accounts/${emailAccountId}/`)
      return data as SmartleadMailboxDetails
    } catch (error) {
      console.error(`Error fetching mailbox details for ${emailAccountId}:`, error)
      throw error
    }
  }

  async updateMailbox(emailAccountId: number, updates: MailboxUpdatePayload): Promise<SmartleadMailboxDetails> {
    try {
      console.log(`🔄 Updating mailbox ${emailAccountId} in Smartlead:`, updates)
      
      const data = await this.makeRequest(`/email-accounts/${emailAccountId}/`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      })

      console.log(`✅ Successfully updated mailbox ${emailAccountId} in Smartlead`)
      return this.fetchMailboxDetails(emailAccountId)
    } catch (error) {
      console.error(`Error updating mailbox ${emailAccountId}:`, error)
      throw error
    }
  }

  async toggleWarmup(emailAccountId: number, enabled: boolean): Promise<{ ok: boolean; message: string; warmupKey?: string; emailAccountId: number }> {
    try {
      console.log(`🔄 ${enabled ? 'Enabling' : 'Disabling'} warmup for mailbox ${emailAccountId} in Smartlead`)
      
      const data = await this.makeRequest(`/email-accounts/${emailAccountId}/warmup`, {
        method: 'POST',
        body: JSON.stringify({ warmup_enabled: enabled })
      })

      console.log(`✅ Successfully ${enabled ? 'enabled' : 'disabled'} warmup for mailbox ${emailAccountId}`)
      return data
    } catch (error) {
      console.error(`Error toggling warmup for mailbox ${emailAccountId}:`, error)
      throw error
    }
  }

  async fetchWarmupStats(emailAccountId: number): Promise<SmartleadWarmupStats> {
    try {
      const data = await this.makeRequest(`/email-accounts/${emailAccountId}/warmup-stats`)
      return {
        email: data.email,
        warmup_reputation: data.warmup_reputation || 0,
        warmup_volume: data.warmup_volume || 0,
        warmup_enabled: data.warmup_enabled || false,
        daily_warmup_limit: data.daily_warmup_limit || 10,
        warmup_reply_rate: data.warmup_reply_rate || 0,
        warmup_folder_percentage: {
          inbox: data.warmup_folder_percentage?.inbox || 0,
          spam: data.warmup_folder_percentage?.spam || 0,
          promotion: data.warmup_folder_percentage?.promotion || 0,
          social: data.warmup_folder_percentage?.social || 0
        },
        warmup_age_days: data.warmup_age_days,
        warmup_sending_volume: data.warmup_sending_volume,
        warmup_receiving_volume: data.warmup_receiving_volume
      }
    } catch (error) {
      console.error(`Error fetching warmup stats for ${emailAccountId}:`, error)
      return {
        email: '',
        warmup_reputation: 0,
        warmup_volume: 0,
        warmup_enabled: false,
        daily_warmup_limit: 10,
        warmup_reply_rate: 0,
        warmup_folder_percentage: { inbox: 0, spam: 0, promotion: 0, social: 0 }
      }
    }
  }

  async fetchAllMailboxes(progressCallback?: FetchingProgressCallback): Promise<SmartleadMailboxDetails[]> {
    try {
      console.log('🔄 Fetching all mailboxes from Smartlead with enhanced progress tracking...')
      
      const allMailboxes: SmartleadMailboxDetails[] = []
      let offset = 0
      const limit = 100
      let batchNumber = 1
      
      while (true) {
        console.log(`📧 Fetching mailbox batch ${batchNumber} (offset: ${offset}, limit: ${limit})...`)
        
        // Report progress before making API call
        if (progressCallback) {
          progressCallback({
            phase: 'fetching',
            currentBatch: batchNumber,
            totalFetched: allMailboxes.length,
            batchSize: limit
          })
        }
        
        // Fetch this batch of mailboxes
        const data = await this.makeRequest(`/email-accounts?offset=${offset}&limit=${limit}`)
        
        if (!Array.isArray(data)) {
          console.error('❌ Expected array from email-accounts endpoint, got:', typeof data)
          break
        }

        console.log(`📦 Received batch ${batchNumber}: ${data.length} mailboxes`)
        
        if (data.length === 0) {
          console.log('🏁 No more mailboxes found. Pagination complete.')
          break
        }

        // Add mailboxes from this batch directly as they come from API
        allMailboxes.push(...data.map(item => item as SmartleadMailboxDetails))
        console.log(`📊 Batch ${batchNumber}: Added ${data.length} mailboxes. Total so far: ${allMailboxes.length}`)

        // Report progress after processing batch
        if (progressCallback) {
          progressCallback({
            phase: 'fetching',
            currentBatch: batchNumber,
            totalFetched: allMailboxes.length,
            batchSize: limit
          })
        }

        // Check if we got fewer results than requested (last batch)
        if (data.length < limit) {
          console.log(`🏁 Last batch contained ${data.length} mailboxes. Pagination complete.`)
          break
        }

        // Prepare for next batch
        offset += limit
        batchNumber++
        
        // Rate limiting - wait 5 seconds between batches
        console.log('⏱️ Waiting 5s before next batch for rate limiting...')
        await new Promise(resolve => setTimeout(resolve, 5000))
      }

      console.log(`🎉 FINAL RESULTS: Successfully fetched ${allMailboxes.length} total mailboxes across ${batchNumber} batches`)
      return allMailboxes
    } catch (error) {
      console.error('❌ Error fetching all mailboxes:', error)
      return []
    }
  }

  async syncAllMailboxData(progressCallback?: FetchingProgressCallback): Promise<{ [email: string]: EnhancedMailboxData }> {
    console.log('🔄 Starting comprehensive mailbox data sync...')
    
    try {
      // Fetch ALL mailboxes using the updated pagination method with progress reporting
      const allMailboxes = await this.fetchAllMailboxes(progressCallback)
      
      if (allMailboxes.length === 0) {
        console.warn('⚠️ No mailboxes found in Smartlead')
        return {}
      }

      console.log(`📊 Processing ${allMailboxes.length} mailboxes from Smartlead`)

      // Create simplified enhanced data structure for bulk processing
      const enhancedData: { [email: string]: EnhancedMailboxData } = {}

      for (const mailbox of allMailboxes) {
        try {
          // Use from_email or username as the primary email field
          const emailValue = mailbox.from_email || mailbox.username || mailbox.email || `unknown-${mailbox.id}@placeholder.com`
          
          // Create simplified enhanced data structure
          enhancedData[emailValue] = {
            basic_details: mailbox,
            warmup_stats: {
              email: emailValue,
              warmup_reputation: 0,
              warmup_volume: 0,
              warmup_enabled: mailbox.warmup_details !== null,
              daily_warmup_limit: 10,
              warmup_reply_rate: 0,
              warmup_folder_percentage: { inbox: 0, spam: 0, promotion: 0, social: 0 }
            },
            overall_stats: {
              email: emailValue,
              total_sent: mailbox.total_sent || 0,
              total_replies: mailbox.total_replied || 0,
              total_bounces: mailbox.total_bounced || 0,
              reply_rate: 0,
              bounce_rate: 0,
              open_rate: 0,
              click_rate: 0
            },
            health_metrics: {
              email: emailValue,
              health_score: mailbox.reputation_score || 100,
              reputation_score: mailbox.reputation_score || 100,
              deliverability_rate: 0,
              spam_rate: 0,
              bounce_rate: 0,
              reply_rate: 0,
              open_rate: 0
            },
            campaign_associations: [],
            provider_performance: {
              provider: this.getProviderFromEmail(emailValue),
              total_sent: mailbox.total_sent || 0,
              total_replies: mailbox.total_replied || 0,
              reply_rate: 0,
              bounce_rate: 0,
              health_score: mailbox.reputation_score || 100
            },
            in_smartlead: true,
            last_synced: new Date().toISOString()
          }
        } catch (error) {
          console.error(`Error processing mailbox ${mailbox.id}:`, error)
        }
      }

      console.log(`✅ Comprehensive sync completed for ${Object.keys(enhancedData).length} mailboxes`)
      return enhancedData
    } catch (error) {
      console.error('❌ Error in comprehensive mailbox sync:', error)
      throw error
    }
  }

  private getProviderFromEmail(email: string): string {
    const domain = email.split('@')[1]?.toLowerCase()
    if (domain?.includes('gmail')) return 'Gmail'
    if (domain?.includes('outlook') || domain?.includes('hotmail')) return 'Outlook'
    if (domain?.includes('yahoo')) return 'Yahoo'
    if (domain?.includes('zoho')) return 'Zoho'
    return 'Other'
  }
}

export const enhancedSmartleadApi = new EnhancedSmartleadApi()
